/**
 * @fileoverview Utility for defining display masks for fund details
 * @active This module is actively used across the application for fund data display
 * @see {@link src/components/fundDetailsPage/portfolioPanel/PortfolioPanel.tsx}
 * @see {@link src/components/fundDetailsPage/analysisPanel/AnalysisPanel.tsx}
 */

import LinkedInSvg from "@/assets/svg/linkedIn.svg";

import { formatNumber } from "./formatNumber";

/**
 * Defines the chevron mask for fund summary display
 * @type {Array<{title: string, field: string}>}
 */
export const fundChevronMask = [
  { title: "Fund Type", field: "typeFund" },
  { title: "Vintage", field: "createdAt" },
];

export const fundTabSmallCards = [
  {
    title: "Committed Capital",
    field: "committedCapitalTotal",
  },
  {
    title: "Fee Structure",
    field: ["feeMgmt", "feePerformance"],
  },
  {
    title: "Investment Horizon",
    field: "investmentHorizon",
  },
  {
    title: "Native Currency",
    field: "currencyNative",
  },
];

export const fundTabLargeCard1 = [
  { title: "Fund Manager", field: "fundManagerFirm" },
  { title: "Fund Status", field: "statusFund" },
  { title: "Fund Domicile", field: "fundDomicile" },
  { title: "Fund Structure", field: "fundStructure" },
  { title: "Fund Legal Name", field: "nameBrand" },
];

export const fundTabLargeCard2 = [
  { title: "Total Investments", field: "totalInvestments" },
  { title: "Active Portfolio Count", field: "activePortfolioCount" },
  { title: "Total Portfolio Exits", field: "totalPortfolioExits" },
  { title: "Investment Stages", field: "investmentStages" },
  { title: "Geographic Exposure", field: "geographicExposure" },
];
//-----------------------------------------------------------------

export const analysisTabsBlock1 = (data) => {
  function formatDateToMonthYear(date) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
    };
    return new Date(date).toLocaleDateString("en-US", options);
  }

  return {
    title: { name: "Portfolio Profitability & Fundraising", field: "" },
    image: "/images/fund-details-chart1.png",
    chartText: {
      title: "Profitability Relative to Peers Graph",
      text: `Analyzing all of the known holdings of the ${data?.name} fund, we find their portfolio companies have a relative level of profitability that is comparable to their competitors/peer groups (that is, not better or worse).`,
      noteText: "Learn more about our methodology",
    },
    // mainParams: [
    //   {
    //     title: "Profitability Relative to Peers",
    //     value: data?.relativeProfitability?.valueResearchDetail,
    //     secondValue: `As of ${formatDateToMonthYear(
    //       data?.relativeProfitability?.asOfDate
    //     )}`,
    //     statusTag: data?.relativeProfitability?.confidence,
    //     graph: data?.relativeProfitability?.qraph,
    //   },
    // ],
    listParams: [
      {
        title: "Profitability Relative to Peers",
        value: data?.relativeProfitability?.valueResearchDetail,
        secondValue: data?.relativeProfitability?.asOfDate
          ? `As of ${formatDateToMonthYear(
              data?.relativeProfitability?.asOfDate
            )}`
          : "",
        statusTag: data?.relativeProfitability?.confidence,
        graph: data?.relativeProfitability?.qraph,
        schema: "analysisTabsBlock1",
      },
      {
        title: "Portfolio Company Profitability",
        value: data?.profitability?.valueResearchDetail,
        secondValue: data?.profitability?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.profitability?.asOfDate)}`
          : "",
        statusTag: data?.profitability?.confidence,
        graph: data?.profitability?.qraph,
        schema: "analysisTabsBlock1",
      },
      {
        title: "Industry Economics",
        value: data?.industryEconomics?.valueResearchDetail,
        secondValue: data?.industryEconomics?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.industryEconomics?.asOfDate)}`
          : "",
        statusTag: "",
        graph: data?.industryEconomics?.qraph,
        schema: "analysisTabsBlock1",
      },
      {
        title: "Runway",
        value: data?.impliedRunway?.valueResearchDetail,
        secondValue: data?.impliedRunway?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.impliedRunway?.asOfDate)}`
          : "",
        statusTag: data?.impliedRunway?.confidence,
        graph: data?.impliedRunway?.qraph,
        schema: "analysisTabsBlock1",
      },
      {
        title: "Fundraising Needs",
        value: data?.fundraisingNeeds?.valueResearchDetail,
        secondValue: data?.fundraisingNeeds?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.fundraisingNeeds?.asOfDate)}`
          : "",
        statusTag: data?.fundraisingNeeds?.confidence,
        graph: data?.fundraisingNeeds?.qraph,
        schema: "analysisTabsBlock1",
      },
      {
        title: "Fundraising Status in Sector",
        value: data?.fundraisingSectorStatus?.valueResearchDetail,
        valueAdd: data?.fundraisingSectorStatus?.typeResearchDetail,
        secondValue: data?.fundraisingSectorStatus?.asOfDate
          ? `As of ${formatDateToMonthYear(
              data?.fundraisingSectorStatus?.asOfDate
            )}`
          : "",
        statusTag: data?.fundraisingSectorStatus?.confidence,
        graph: data?.fundraisingSectorStatus?.qraph,
        schema: "analysisTabsBlock1",
      },
    ],
  };
};

export const analysisTabsBlock2 = (data) => {
  function formatDateToMonthYear(date) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
    };
    return new Date(date?.slice(0, 10)).toLocaleDateString("en-US", options);
  }
  return {
    title: { name: "Portfolio Holdings Analysis", field: "" },
    image: "/images/fund-details-chart2.png",
    chartText: {
      title: "Annual Company Revenue",
      text: `Investors in their 20s, 30s and 40s all maintain about a 43% allocation of U.S. stocks and 11-12% allocation of international stocks in their financial portfolios. Investors in their 50s and 60s keep between 39% and 42% of their portfolio assets in U.S. stocks and about 10% in international stocks.`,
      noteText: "Learn more about our methodology",
    },
    // mainParams: [
    //   {
    //     title: "Average Company Revenue",
    //     value: "$2 to $10 Million USD",
    //     secondValue: "As of Sep 2023",
    //     statusTag: "High",
    //   },
    // ],
    listParams: [
      {
        title: "Average Company Revenue",
        value: data?.revenueEstimate?.valueResearchDetail,
        secondValue: data?.revenueEstimate?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.revenueEstimate?.asOfDate)}`
          : "",
        statusTag: data?.revenueEstimate?.confidence,
        graph: data?.revenueEstimate?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Revenue Growth",
        value: data?.revenueGrowthEstimate?.valueResearchDetail,
        secondValue: data?.revenueGrowthEstimate?.asOfDate
          ? `As of ${formatDateToMonthYear(
              data?.revenueGrowthEstimate?.asOfDate
            )}`
          : "",
        statusTag: data?.revenueGrowthEstimate?.confidence,
        graph: data?.revenueGrowthEstimate?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Customer Retention Rate",
        value: data?.customerRetentionRate?.valueResearchDetail,
        valueAdd: "+",
        secondValue: data?.customerRetentionRate?.asOfDate
          ? `As of ${formatDateToMonthYear(
              data?.customerRetentionRate?.asOfDate
            )}`
          : "",
        statusTag: data?.customerRetentionRate?.confidence,
        graph: data?.customerRetentionRate?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Customers",
        value: data?.numberOfCustomers?.valueResearchDetail,
        secondValue: data?.numberOfCustomers?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.numberOfCustomers?.asOfDate)}`
          : "",
        statusTag: data?.numberOfCustomers?.confidence,
        graph: data?.numberOfCustomers?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Round Value",
        value: formatNumber(data?.roundValue?.value),
        secondValue: data?.roundValue?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.roundValue?.asOfDate)}`
          : "",
        statusTag: "",
        statusText: data?.roundValue?.round,
        graph: data?.roundValue?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Most Common Round",
        value: data?.round?.value,
        secondValue: data?.round?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.round?.asOfDate)}`
          : "",
        statusTag: "",
        statusText: `Last Deal: ${formatDateToMonthYear(
          data?.round?.lastDeal
        )}`,
        graph: data?.round?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Total Raised",
        value: formatNumber(data?.totalRaised?.value),
        secondValue: data?.totalRaised?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.totalRaised?.asOfDate)}`
          : "",
        statusTag: "",
        graph: data?.totalRaised?.qraph,
        schema: "analysisTabsBlock2",
      },
      // {
      //   title: "Average Valuation",
      //   value: "$29.2 Million USD",
      //   secondValue: "As of Aug 2023",
      //   statusTag: "Medium",
      //   schema: "analysisTabsBlock2",
      // },
      {
        title: "Average Growth Score",
        value: data?.growthScoreAggregate?.valueResearchDetail,
        secondValue: data?.growthScoreAggregate?.asOfDate
          ? `As of ${formatDateToMonthYear(
              data?.growthScoreAggregate?.asOfDate
            )}`
          : "",
        statusTag: data?.growthScoreAggregate?.confidence,
        graph: data?.growthScoreAggregate?.qraph,
        schema: "analysisTabsBlock2",
      },
      {
        title: "Average Risk Score",
        value: data?.riskScoreAggregate?.valueResearchDetail,
        secondValue: data?.riskScoreAggregate?.asOfDate
          ? `As of ${formatDateToMonthYear(data?.riskScoreAggregate?.asOfDate)}`
          : "",
        statusTag: data?.riskScoreAggregate?.confidence,
        graph: data?.riskScoreAggregate?.qraph,
        schema: "analysisTabsBlock2",
      },
    ],
  };
};

export const fundLeaderShipMask = [
  {
    title: "Name",
    field0: "person",
    field1: "nameFull",
    imageField0: "person",
    imageField1: "personPicture",
  },
  { title: "Title", field0: "corporateTitle", field1: "personCompanyTitle" },
  { title: "Level", field0: "corporateTitle", field1: "personEntityJobLevel" },
  {
    title: "Function",
    field0: "corporateTitle",
    field1: "personEntityFunction",
  },
  {
    title: "Location",
    field0: "person",
    field1: "addresses",
    field2: "address",
  },
  {
    title: "LinkedIn",
    field0: "person",
    field1: "webUrl",
    field2: "urlLinkedin",
  },
];

/**
 * Defines the investment mask for fund investments display
 * Note: "Post Valuation ($)", "Type", and "Status" columns have been intentionally removed
 * @type {Array<{title: string, field0: string, field1?: string}>}
 */
export const fundInvestmentsMask = [
  { title: "Company Name", field0: "fundraiseTransaction", field1: "round" },
  { title: "Date", field0: "fundraiseTransaction", field1: "dateAnnounced" },
  { title: "Lead Investor", field0: "leadInvestor" },
  {
    title: "Investor(s)",
    field0: "fundraiseTransaction",
    field1: "numberOfInvestors",
  },
  {
    title: "Invested ($)",
    field0: "fundraiseTransaction",
    field1: "amountRaised",
  },
  {
    title: "Total Round ($)",
    field0: "fundraiseTransaction",
    field1: "",
  },
];
/**
 * Defines the view fund detail mask for fund details display
 * Note: "Post Valuation ($)", "Type", and "Status" columns have been intentionally removed
 * @type {Array<{title: string, field0: string}>}
 */
export const viewFundDetailMask = [
  { title: "Company Name", field0: "round" },
  { title: "Date", field0: "dateFundingComplete" },
  { title: "Lead Investor", field0: "investorType" },
  { title: "Investor(s)", field0: "numberOfInvestors" },
  { title: "Amount Raised ($)", field0: "amountRaised" },
  { title: "Raised to Date ($)", field0: "raisedToDate" },
];

//-----------------------------------------------------------------

export const contactsTabsBlockEmployees = (data) => {
  return {
    title: { name: data?.name, field: "" },
    tableContent: [
      {
        title: "Name",
        field0: "person",
        field1: "nameFirst",
        field2: "nameLast",
        imageField0: "person",
        imageField1: "personPicture",
        linkField0: "person",
        linkField1: "slug",
      },
      {
        title: "Title",
        field0: "personEntityTitle",
      },
      {
        title: "Level",

        // field0: "person",
        field0: "personEntityJobLevelText",
      },
      {
        title: "Function",
        field0: "personEntityFunction",
      },
      {
        title: "Location",
        field: "person",
        field0: "resDetailAddress",
        field1: "geoLocationCity",
        field2: "geoLocationStateAbbrev",
        field3: "geoLocationCountryAbbrev",
      },
      {
        title: "LinkedIn",
        field0: "person",
        field1: "urlLinkedin",
        icon: <LinkedInSvg />,
      },
    ],
  };
};

export const jobTitleEmployees = (data) => {
  return {
    title: { name: data?.name, field: "" },
    tableContent: [
      {
        title: "Title",
        field0: "personCompanyTitle",
      },
      {
        title: "Location",
        field0: "person",
        field1: [
          "geoLocationCity",
          "geoLocationStateAbbrev",
          "geoLocationCountryAbbrev",
        ],
      },
      {
        title: "Low Range",
        field0: "person",
        field1: "low_range",
      },
      {
        title: "Mid Range",
        field0: "person",
        field1: "low_range",
      },
      {
        title: "High Range",
        field0: "person",
        field1: "hide_range",
      },
      {
        title: "Benefits / Overhead Estimate",
        field0: "person",
        field1: "benefits",
      },
    ],
  };
};
